import { useState, useEffect } from "react";
import { getCompanyCodes, getResourceMasters, createSalaryUpdation } from "../Api/SalaryUpdation";
import { Container, Form, Button, Row, Col } from "react-bootstrap";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Sample14 from "../Components/Logo";

const SalaryUpdation = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [companyCodes, setCompanyCodes] = useState([]);
  const [resourceMasters, setResourceMasters] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(true);

  
  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const userId = userDetails?.userId;

  const [salaryData, setSalaryData] = useState({
    companyCode: "",
    resourceId: "",
    validFrom: "",
    validTo: "9999-12-31",
    basicInHand: "",
    pf: "",
    esic: "",
    grossSalary: ""
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        setFetchLoading(true);
        const [companyData, resourceData] = await Promise.all([
          getCompanyCodes(),
          getResourceMasters()
        ]);
        
        setCompanyCodes(companyData);
        setResourceMasters(resourceData);
      } catch (error) {
        toast.error("Error loading dropdown data");
        console.error("Error fetching dropdown data:", error);
      } finally {
        setFetchLoading(false);
      }
    };

    fetchDropdownData();
  }, []);

  const validateForm = () => {
    const newErrors = {};

    if (!salaryData.companyCode) newErrors.companyCode = "Company Code is required";
    if (!salaryData.resourceId) newErrors.resourceId = "Resource ID is required";
    if (!salaryData.validFrom) newErrors.validFrom = "Valid From date is required";
    
    if (!salaryData.basicInHand || parseFloat(salaryData.basicInHand) <= 0) {
      newErrors.basicInHand = "Basic In Hand must be greater than 0";
    }
    
    if (!salaryData.pf || parseFloat(salaryData.pf) < 0) {
      newErrors.pf = "PF cannot be negative";
    }
    
    if (!salaryData.esic || parseFloat(salaryData.esic) < 0) {
      newErrors.esic = "ESIC cannot be negative";
    }
    
    if (!salaryData.grossSalary || parseFloat(salaryData.grossSalary) <= 0) {
      newErrors.grossSalary = "Gross Salary must be greater than 0";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSalaryData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const handleNumericChange = (e) => {
    const { name, value } = e.target;
    // Allow only numbers and decimal point
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setSalaryData(prev => ({
        ...prev,
        [name]: value
      }));

      // Clear error when user starts typing
      if (errors[name]) {
        setErrors(prev => ({
          ...prev,
          [name]: ""
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error("Please fix the validation errors");
      return;
    }

    setLoading(true);

    // Convert numeric values to numbers
    const payload = {
      ...salaryData,
      basicInHand: parseFloat(salaryData.basicInHand),
      pf: parseFloat(salaryData.pf),
      esic: parseFloat(salaryData.esic),
      grossSalary: parseFloat(salaryData.grossSalary)
    };

    try {
      await createSalaryUpdation(payload, userId);
      toast.success("🦄 Salary Updated Successfully");
      setSalaryData({
        companyCode: "",
        resourceId: "",
        validFrom: "",
        validTo: "9999-12-31",
        basicInHand: "",
        pf: "",
        esic: "",
        grossSalary: ""
      });
    } catch (error) {
      toast.error("🦄 Error creating salary updation");
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <div className="main-content expand">
        <Container fluid className="fields-container">
          <div className="text-center">Loading dropdown data...</div>
        </Container>
      </div>
    );
  }

  return (
    <>
      <div className="main-content expand">
        <Container fluid className="fields-container">
          <h2 className="fields-title">Salary Updation</h2>

          <Form onSubmit={handleSubmit} className="fields-form">
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Company Code <span className="text-red">**</span></Form.Label>
                  <Form.Select 
                    name="companyCode" 
                    value={salaryData.companyCode} 
                    onChange={handleChange} 
                    required
                    isInvalid={!!errors.companyCode}
                  >
                    <option value="">Select Company Code</option>
                    {companyCodes.map((company) => (
                      <option key={company.companyCode} value={company.companyCode}>
                        {company.companyCode} - {company.name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.companyCode}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Resource ID <span className="text-red">**</span></Form.Label>
                  <Form.Select 
                    name="resourceId" 
                    value={salaryData.resourceId} 
                    onChange={handleChange} 
                    required
                    isInvalid={!!errors.resourceId}
                  >
                    <option value="">Select Resource</option>
                    {resourceMasters.map((resource) => (
                      <option key={resource.resourceId} value={resource.resourceId}>
                        {resource.resourceId} - {resource.name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.resourceId}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Valid From <span className="text-red">**</span></Form.Label>
                  <Form.Control 
                    type="date" 
                    name="validFrom" 
                    value={salaryData.validFrom} 
                    onChange={handleChange} 
                    required 
                    isInvalid={!!errors.validFrom}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.validFrom}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Valid To</Form.Label>
                  <Form.Control 
                    type="date" 
                    name="validTo" 
                    value={salaryData.validTo} 
                    onChange={handleChange}
                    disabled
                    className="bg-light"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Basic In Hand <span className="text-red">**</span></Form.Label>
                  <Form.Control 
                    type="text" 
                    name="basicInHand" 
                    value={salaryData.basicInHand} 
                    onChange={handleNumericChange}
                    required 
                    isInvalid={!!errors.basicInHand}
                    placeholder="Enter amount"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.basicInHand}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>PF <span className="text-red">**</span></Form.Label>
                  <Form.Control 
                    type="text" 
                    name="pf" 
                    value={salaryData.pf} 
                    onChange={handleNumericChange}
                    required 
                    isInvalid={!!errors.pf}
                    placeholder="Enter PF amount"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.pf}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>ESIC <span className="text-red">**</span></Form.Label>
                  <Form.Control 
                    type="text" 
                    name="esic" 
                    value={salaryData.esic} 
                    onChange={handleNumericChange}
                    required 
                    isInvalid={!!errors.esic}
                    placeholder="Enter ESIC amount"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.esic}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Gross Salary <span className="text-red">**</span></Form.Label>
                  <Form.Control 
                    type="text" 
                    name="grossSalary" 
                    value={salaryData.grossSalary} 
                    onChange={handleNumericChange}
                    required 
                    isInvalid={!!errors.grossSalary}
                    placeholder="Enter gross salary"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.grossSalary}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <div className="fields-btn-container">
              <Button variant="primary" type="submit" className="fields-submit" disabled={loading}>
                {loading ? "Saving..." : "Save Salary Updation"}
              </Button>
            </div>
          </Form>
        </Container>
      </div>

      {loading && (
        <div className="loading-overlay">
          <Sample14 />
        </div>
      )}
    </>
  );
};

export default SalaryUpdation;